import { store } from 'redux/store';
import i18n from 'i18n/config';
import { useCallback, useEffect, useState } from 'react';

import { HoldingClass } from 'types';
import { getHoldingClasses } from 'modules/partners/api/getHoldingClasses';
import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { translate } from 'hooks/useTranslate';

type GetOptionsProps = {
  holdingClasses: HoldingClass[];
  includeShares: boolean;
};

type UseGetClassOptionsProps = {
  societyId: string;
  includeShares?: boolean;
  /** If false, no first class is selected when loading (e.g. canje origin) */
  autoSelectFirst?: boolean;
};

const FIELDS = 'name sharesCount numerationOption';
const FIELD_TO_POPULATE = ['numerationOption'];

const getOptions = ({
  holdingClasses,
  includeShares,
}: GetOptionsProps): CustomAsyncSelectOption[] => {
  const options: CustomAsyncSelectOption[] = holdingClasses.map(
    (holdingClass) => ({
      value: holdingClass._id,
      label: includeShares
        ? `${holdingClass.name} | ${
            holdingClass.sharesCount?.actual
          } ${translate('shares')}`
        : holdingClass.name,
      numerationOptionName:
        (holdingClass?.numerationOption as HoldingClass)?.name || '1',
    })
  );

  return options;
};

export const useGetHoldingClassOptionsForOperationModal = ({
  societyId,
  includeShares = false,
  autoSelectFirst = true,
}: UseGetClassOptionsProps) => {
  const [selectedHoldingClass, setSelectedHoldingClass] = useState<string>('');
  const [selectedHoldingClassOption, setSelectedHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [defaultHoldingClassOption, setDefaultHoldingClassOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [holdingClassOptions, setHoldingClassOptions] = useState<
    CustomAsyncSelectOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleClassOptionChange = (
    selectedOption: CustomAsyncSelectOption | null
  ) => {
    setSelectedHoldingClassOption(selectedOption);
    setSelectedHoldingClass(selectedOption?.value || '');
  };

  const handleUpdateHoldingClasssOptionShares = (
    holdingClassId: string,
    sharesCount: number,
    isSelectedOption: boolean
  ) => {
    setHoldingClassOptions((prevOptions) =>
      prevOptions.map((option) => {
        if (option.value === holdingClassId) {
          const prevSharesCount = +option.label.split('|')[1].split(' ')[1];

          const newOption = {
            ...option,
            label: `${option.label.split('|')[0]} | ${
              prevSharesCount + sharesCount
            } ${i18n.t('shares')}`,
          };

          if (isSelectedOption) {
            setSelectedHoldingClassOption(newOption);
          }
          return {
            ...newOption,
          };
        }
        return option;
      })
    );
  };

  const fetchHoldingClasses = useCallback(async () => {
    if (!societyId) return;
    try {
      setIsLoading(true);

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: { fields: FIELDS, fieldsToPopulate: FIELD_TO_POPULATE },
        })
      );

      const holdingClassOptions = getOptions({ holdingClasses, includeShares });

      setHoldingClassOptions(holdingClassOptions);
      if (autoSelectFirst && holdingClassOptions[0]) {
        setSelectedHoldingClassOption(holdingClassOptions[0]);
        setDefaultHoldingClassOption(holdingClassOptions[0]);
        setSelectedHoldingClass(holdingClassOptions[0].value);
      } else {
        setSelectedHoldingClassOption(null);
        setDefaultHoldingClassOption(null);
        setSelectedHoldingClass('');
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [autoSelectFirst, includeShares, societyId]);

  const handleFetchHoldingClassByTerm = async (searchTerm: string) => {
    if (!societyId) return undefined;
    if (!searchTerm) return;
    try {
      setIsLoading(true);

      const holdingClasses = await store.dispatch(
        getHoldingClasses({
          societyId,
          params: {
            fields: FIELDS,
            fieldsToPopulate: FIELD_TO_POPULATE,
            searchTerm,
          },
        })
      );

      const holdingClassOptions = getOptions({ holdingClasses, includeShares });

      setIsLoading(false);

      return holdingClassOptions;
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  };

  useEffect(() => {
    fetchHoldingClasses();
  }, [fetchHoldingClasses]);

  return {
    data: {
      selectedHoldingClass,
      selectedHoldingClassOption,
      holdingClassOptions,
      defaultHoldingClassOption,
    },
    actions: {
      handleClassOptionChange,
      handleUpdateHoldingClasssOptionShares,
      handleFetchHoldingClassByTerm,
    },
    isLoading,
    isError,
  };
};
